import React, { useState, useEffect, useRef } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import CookieConsent from "react-cookie-consent";
import 'flag-icons/css/flag-icons.min.css';
import Logotyp3 from './logotyp3.svg';
import Silk from './Silk';
import TextType from './TextType';
import GooeyNav from './GooeyNav';
import emailjs from 'emailjs-com';
import PolitykaPrywatnosci from './PolitykaPrywatnosci';

const App: React.FC = () => {
    return (
      <Router>
        <Routes>
          <Route path="/" element={<NovaWitryny />} />
          <Route path="/polityka-prywatnosci" element={<PolitykaPrywatnosci />} />
          {/* fallback route */}
          <Route path="*" element={<NovaWitryny />} />
        </Routes>
      </Router>
    );
  };

const NovaWitryny: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<'PL' | 'DE' | 'EN'>('PL');
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVisible, setIsVisible] = useState({
    hero: false,
    services: false,
    process: false,
    compatibility: false,
    testimonials: false,
    about: false,
    contact: false,
    packages: false,
    portfolio: false
  });
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentLightboxImage, setCurrentLightboxImage] = useState('');
  const [lightboxImages, setLightboxImages] = useState<string[]>([]);

  const observerRefs = {
    hero: useRef(null),
    services: useRef(null),
    process: useRef(null),
    compatibility: useRef(null),
    testimonials: useRef(null),
    about: useRef(null),
    contact: useRef(null),
    packages: useRef(null),
    portfolio: useRef(null)
  };

  // Karuzela przeglądarek
  const browsers = [
    { name: "Chrome", icon: "fab fa-chrome", color: "#DB4437" },
    { name: "Mozilla Firefox", icon: "fab fa-firefox-browser", color: "#FF9500" },
    { name: "Safari", icon: "fab fa-safari", color: "#000000" },
    { name: "Microsoft Edge", icon: "fab fa-edge", color: "#0078D7" },
    { name: "Opera", icon: "fab fa-opera", color: "#FF1B2D" },
    { name: "Brave", icon: "fas fa-shield-alt", color: "#FB542B" }
  ];

  // Projekty GitHub
  const githubProjects = [
    {
      name: "Company Website for a Foundation",
      description: "Nowoczesna strona portfolio z React i TypeScript dla Fundacji Spełnione Marzenie",
      githubUrl: "https://github.com/CBTEnjoyer/RIOO-fundacja",
      technologies: ["React", "TypeScript", "Tailwind CSS"],
      image: "/fundacja.png",
      demoUrl: "https://rioo.vercel.app"
    },
    {
      name: "Portfolio Website - Barber Shop",
      description: "Nowoczesna strona portfolio z React i TypeScript dla salonu fryzjerskiego",
      githubUrl: "https://github.com/CBTEnjoyer/majster-barber",
      technologies: ["React", "TypeScript", "Tailwind CSS"],
      image: "/majster.png",
      demoUrl: "https://majster-barber.vercel.app"
    },
    {
      name: "Full Portfolio website for NOVA HLS - mother company",
      description: "Nowoczesna strona portfolio z React i TypeScript dla firmy remontowej",
      githubUrl: "https://github.com/CBTEnjoyer/nova-hls",
      technologies: ["React", "TypeScript", "Tailwind CSS"],
      image: "/novahls_git.png",
      demoUrl: "https://novahls.vercel.app"
    },
    {
      name: "Simple Python CMS - PDF Generator with all calculations for travel agencies (offer, confirmation, invoice)",
      description: "Prosta aplikacja desktopowa CMS w Pythonie",
      githubUrl: "https://github.com/CBTEnjoyer/Travel-Agency-CMS",
      technologies: ["Python", "Tkinter", "SQLite"],
      image: "/cms.png",
      demoUrl: "https://github.com/CBTEnjoyer/Travel-Agency-CMS/tree/main/assets"
    },
    {
      name: "Portfolio Website for a Tattoo Studio",
      description: "Nowoczesna strona portfolio z React i TypeScript dla studia tatuażu",
      githubUrl: "https://github.com/CBTEnjoyer/tattoo-studio",
      technologies: ["React", "TypeScript", "Tailwind CSS"],
      image: "mizerykordia.png",
      demoUrl: "https://tattoo-studio-iota.vercel.app"
    },
    {
      name: "Full Portfolio website for NOVA Podróże - travel agency branch",
      description: "Nowoczesna strona portfolio z React i TypeScript dla biura podróży",
      githubUrl: "https://github.com/CBTEnjoyer/nova-podroze",
      technologies: ["React", "TypeScript", "Tailwind CSS"],
      image: "/biuro_podrozy.png",
      demoUrl: "https://nova-podroze.vercel.app"
    }
  ];

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1500);
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.2
    };
    
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const section = entry.target.getAttribute('data-section');
          if (section) {
            setIsVisible(prev => ({ ...prev, [section]: true }));
          }
        }
      });
    };
    
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    
    Object.keys(observerRefs).forEach(key => {
      if (observerRefs[key as keyof typeof observerRefs].current) {
        observer.observe(observerRefs[key as keyof typeof observerRefs].current!);
      }
    });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const toggleLangDropdown = () => setIsLangDropdownOpen(!isLangDropdownOpen);
  const selectLanguage = (lang: 'PL' | 'DE' | 'EN') => {
    setCurrentLanguage(lang);
    setIsLangDropdownOpen(false);
  };

  const openLightbox = (image: string, images: string[], index: number = 0) => {
    setCurrentLightboxImage(images[index]);
    setLightboxImages(images);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setCurrentLightboxImage('');
    setLightboxImages([]);
  };

  const navigateLightbox = (direction: 'next' | 'prev') => {
    const currentIndex = lightboxImages.indexOf(currentLightboxImage);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % lightboxImages.length;
    } else {
      newIndex = (currentIndex - 1 + lightboxImages.length) % lightboxImages.length;
    }
    
    setCurrentLightboxImage(lightboxImages[newIndex]);
  };

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    if (targetId === '#') return;
    const targetElement = document.querySelector(targetId);
    if (targetElement) {
      window.scrollTo({
        top: targetElement.getBoundingClientRect().top + window.pageYOffset - 80,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get('name'),
      email: formData.get('email'),
      company: formData.get('company'),
      message: formData.get('message')
    };

    // Wysyłanie przez EmailJS
    emailjs.send(
      'service_ka994iu',
      'template_kbdp0ih',
      {
        from_name: data.name,
        from_email: data.email,
        company: data.company,
        message: data.message,
        to_email: 'witryny@novahls.pl'
      },
      'cJrVfkwgLlpQ3y1r6'
    )
    .then((result) => {
      console.log('Email sent successfully:', result.text);
      setIsSubmitting(false);
      setIsFormSubmitted(true);
      (e.target as HTMLFormElement).reset();
      
      setTimeout(() => setIsFormSubmitted(false), 5000);
    })
    .catch((error) => {
      console.error('Error sending email:', error);
      setIsSubmitting(false);
    });
  };

  const translations = {
    PL: {
      nav: { home: "Strona Główna", services: "Usługi", process: "Proces", compatibility: "Kompatybilność", testimonials: "Opinie", about: "O Nas", contact: "Kontakt", quote: "Wyceń projekt", packages: "Pakiety", portfolio: "Portfolio" },
      hero: { title: "", subtitle: "Profesjonalne projektowanie i tworzenie stron internetowych", cta: "Stwórzmy razem Twoją wymarzoną stronę", servicesBtn: "Nasze usługi", contactBtn: "Skontaktuj się" },
      services: { 
        title: "Nasze Usługi", 
        description: "Oferujemy kompleksowe usługi projektowania i tworzenia stron internetowych dostosowane do Twoich potrzeb.",
        webDesign: { title: "Projektowanie stron", description: "Nowoczesne, responsywne i estetyczne projekty stron internetowych.", items: ["Projektowanie UI/UX","Responsywność RWD","Optymalizacja konwersji","Projektowanie graficzne"] },
        webDev: { title: "Tworzenie stron", description: "Programowanie stron internetowych z wykorzystaniem najnowszych technologii.", items: ["Strony WWW","W strukturze one-page lub multi-page","Kompatybilność z urządzeniami i przeglądarki","Optymalizacja wydajności"] },
        seo: { title: "Pozycjonowanie SEO", description: "Zwiększ widoczność swojej strony w wynikach wyszukiwania.", items: ["Audyt SEO","Optymalizacja treści","Link building","Analiza konkurencji"] },
        maintenance: { title: "Wsparcie i utrzymanie", description: "Kompleksowa opieka nad stroną po jej wdrożeniu.", items: ["Aktualizacje i backup","Monitoring bezpieczeństwa","Wsparcie techniczne","Raporty efektywności"] }
      },
      packages: {
        title: "Przykładowe pakiety",
        description: "Oferujemy różne pakiety dostosowane do Twoich potrzeb i budżetu",
        basic: {
          title: "Basic",
          price: "1500-2500 PLN",
          description: "Idealny dla małych firm i osób prywatnych",
          features: ["Strona jednostronna (one-page)", "Responsywny design", "Optymalizacja SEO podstawowa", "Integracja z formularzem kontaktowym", "Podstawowe wsparcie techniczne"]
        },
        pro: {
          title: "Pro",
          price: "2500-4000 PLN",
          description: "Doskonały dla rozwijających się firm",
          features: ["Strona wielostronna (multi-page)", "Zaawansowany design", "Optymalizacja SEO zaawansowana", "Integracja z mediami społecznościowymi", "Formularze kontaktowe i newsletter", "7 dni wsparcia technicznego"]
        },
        premium: {
          title: "Premium",
          price: "5000-7500 PLN",
          description: "Kompleksowe rozwiązanie dla wymagających klientów",
          features: ["Strona wielostronna z panelem admina", "Zaawansowany system CMS", "Pełna optymalizacja SEO", "Integracja z systemami płatności", "Analytics i raportowanie", "7 dni wsparcia technicznego", "Szkolenie z obsługi panelu"]
        }
      },
      portfolio: {
        title: "Ostatnie projekty",
        description: "Zobacz moje najnowsze projekty z GitHub",
        viewProject: "Zobacz projekt",
        viewCode: "Kod źródłowy",
        technologies: "Technologie"
      },
      process: {
        title: "Jak pracujemy",
        steps: [
          { title: "Konsultacja i analiza", description: "Rozmawiamy o Twoich potrzebach, celach i oczekiwaniach.", icon: "fas fa-comments" },
          { title: "Projekt koncepcyjny", description: "Przygotowujemy makietę i schemat strony.", icon: "fas fa-pencil-ruler" },
          { title: "Realizacja i kodowanie", description: "Programujemy stronę zgodnie z wcześniejszymi ustaleniami.", icon: "fas fa-code" },
          { title: "Testowanie i optymalizacja", description: "Sprawdzamy działanie strony na różnych urządzeniach i przeglądarkach.", icon: "fas fa-check-double" },
          { title: "Wdrożenie strony", description: "Uruchamiamy stronę i wyjaśniamy jej działanie oraz cechy.", icon: "fas fa-rocket" },
          { title: "Wsparcie posprzedażowe", description: "Zapewniamy opiekę techniczną po zakończeniu projektu w razie ewentualnych błędów czy zmian przez 7 dni.", icon: "fas fa-headset" }
        ]
      },
      compatibility: {
        title: "Pełna kompatybilność",
        description: "Nasze strony działają perfekcyjnie na wszystkich urządzeniach i przeglądarkach",
        subtitle: "Gwarantujemy optymalne działanie na każdym urządzeniu"
      },
      about: { 
        title: "O Nas", 
        description1: "NOVA Witryny to zespół doświadczonych projektantów i programistów z pasją do tworzenia nowoczesnych stron internetowych.", 
        description2: "Specjalizujemy się w tworzeniu responsywnych, wydajnych i funkcjonalnych rozwiązań internetowych, które przynoszą realne korzyści biznesowe.", 
        stats: [
          { value: "120+", label: "Zrealizowanych projektów" },
          { value: "7+", label: "Lat doświadczenia" },
          { value: "117+", label: "Zadowolonych klientów" },
          { value: "7 dni", label: "Posprzedażowe wsparcie techniczne" }
        ]
      },
      contact: { 
        title: "Kontakt", 
        description: "Skontaktuj się z nami, aby omówić swój projekt.", 
        infoTitle: "Kontakt", 
        phone: "Telefon", 
        email: "Email", 
        address: "Adres", 
        formTitle: "Wyślij zapytanie", 
        name: "Imię i nazwisko", 
        company: "Firma", 
        message: "Opis projektu", 
        send: "Wyślij zapytanie" 
      },
      footer: { 
        description: "Profesjonalne projektowanie i tworzenie stron internetowych.", 
        services: "Usługi", 
        company: "Firma", 
        hours: "Godziny otwarcia", 
        rights: "Wszelkie prawa zastrzeżone. Strona autorstwa NOVA Witryny" 
      }
    },
    DE: {
      nav: { home: "Startseite", services: "Dienstleistungen", process: "Prozess", compatibility: "Kompatibilität", testimonials: "Bewertungen", about: "Über uns", contact: "Kontakt", quote: "Angebot einholen", packages: "Pakete", portfolio: "Portfolio" },
      hero: { title: "", subtitle: "Professionelles Webdesign und Webentwicklung", cta: "Lassen Sie uns gemeinsam Ihre Traumwebsite erstellen", servicesBtn: "Unsere Dienstleistungen", contactBtn: "Kontakt aufnehmen" },
      services: { 
        title: "Unsere Dienstleistungen", 
        description: "Wir bieten umfassende Webdesign- und Webentwicklungsdienstleistungen, die auf Ihre Bedürfnisse zugeschnitten sind.",
        webDesign: { title: "Webdesign", description: "Moderne, responsive und ästhetische Webseitendesigns.", items: ["UI/UX-Design","Responsive Design","Konversionsoptimierung","Grafikdesign"] },
        webDev: { title: "Webentwicklung", description: "Webseitenprogrammierung mit den neuesten Technologien.", items: ["Webseiten und Online-Shops","Webanwendungen","Systemintegrationen","Leistungsoptimierung"] },
        seo: { title: "SEO-Optimierung", description: "Erhöhen Sie die Sichtbarkeit Ihrer Website in den Suchergebnissen.", items: ["SEO-Audit","Content-Optimierung","Linkbuilding","Wettbewerbsanalyse"] },
        maintenance: { title: "Support und Wartung", description: "Umfassende Betreuung Ihrer Website nach dem Launch.", items: ["Updates und Backup","Sicherheitsüberwachung","Technischer Support","Leistungsberichte"] }
      },
      packages: {
        title: "Beispielpakete",
        description: "Wir bieten verschiedene Pakete, die auf Ihre Bedürfnisse und Ihr Budget zugeschnitten sind",
        basic: {
          title: "Basic",
          price: "ab 750 EUR",
          description: "Ideal für kleine Unternehmen und Privatpersonen",
          features: ["One-Page-Website", "Responsives Design", "Grundlegende SEO-Optimierung", "Kontaktformular-Integration", "Grundlegender technischer Support"]
        },
        pro: {
          title: "Pro",
          price: "ab 1250 EUR",
          description: "Perfekt für wachsende Unternehmen",
          features: ["Mehrseitige Website", "Erweitertes Design", "Fortgeschrittene SEO-Optimierung", "Social-Media-Integration", "Kontaktformulare und Newsletter", "7 Tage technischer Support"]
        },
        premium: {
          title: "Premium",
          price: "ab 2000 EUR",
          description: "Umfassende Lösung für anspruchsvolle Kunden",
          features: ["Mehrseitige Website mit Admin-Panel", "Erweitertes CMS-System", "Vollständige SEO-Optimierung", "Zahlungssystem-Integration", "Analytics und Berichterstattung", "7 Tage technischer Support", "Schulung zur Bedienung des Panels"]
        }
      },
      portfolio: {
        title: "Meine Projekte",
        description: "Sehen Sie sich meine neuesten GitHub-Projekte an",
        viewProject: "Projekt ansehen",
        viewCode: "Quellcode",
        technologies: "Technologien"
      },
      process: {
        title: "Unser Arbeitsprozess",
        steps: [
          { title: "Beratung und Analyse", description: "Wir besprechen Ihre Bedürfnisse, Ziele und Erwartungen.", icon: "fas fa-comments" },
          { title: "Konzeptentwurf", description: "Wir erstellen ein Mockup und Grafikdesign Ihrer Website.", icon: "fas fa-pencil-ruler" },
          { title: "Umsetzung und Programmierung", description: "Wir programmieren die Website gemäß den vorherigen Absprachen.", icon: "fas fa-code" },
          { title: "Testing und Optimierung", description: "Wir testen die Website auf verschiedenen Geräten und Browsern.", icon: "fas fa-check-double" },
          { title: "Launch und Einarbeitung", description: "Wir starten die Website und schulen Sie in ihrer Bedienung.", icon: "fas fa-rocket" },
          { title: "Nachbetreuung", description: "Wir bieten technische Unterstützung nach Projektabschluss.", icon: "fas fa-headset" }
        ]
      },
      compatibility: {
        title: "Volle Kompatibilität",
        description: "Unsere Websites funktionieren einwandfrei auf allen Geräten und Browsern",
        subtitle: "Wir garantieren eine optimale Leistung auf jedem Gerät"
      },
      about: { 
        title: "Über uns", 
        description1: "NOVA Witryny ist ein Team erfahrener Designer und Entwickler mit Leidenschaft für moderne Webseiten.", 
        description2: "Wir spezialisieren uns auf die Erstellung responsiver, leistungsstarker und funktionaler Web-Lösungen, die echte Geschäftsvorteile bringen.", 
        stats: [
          { value: "120+", label: "Abgeschlossene Projekte" },
          { value: "7+", label: "Jahre Erfahrung" },
          { value: "98%", label: "Zufriedene Kunden" },
          { value: "24h", label: "Technischer Support" }
        ]
      },
      contact: { 
        title: "Kontakt", 
        description: "Kontaktieren Sie uns, um Ihr Projekt zu besprechen.", 
        infoTitle: "Kontakt", 
        phone: "Telefon", 
        email: "E-Mail", 
        address: "Adresse", 
        formTitle: "Anfrage senden", 
        name: "Vor- und Nachname", 
        company: "Firma", 
        message: "Projektbeschreibung", 
        send: "Anfrage senden" 
      },
      footer: { 
        description: "Professionelles Webdesign und Webentwicklung.", 
        services: "Dienstleistungen", 
        company: "Firma", 
        hours: "Öffnungszeiten", 
        rights: "Alle Rechte vorbehalten. Seite erstellt von NOVA Witryny" 
      }
    },
    EN: {
      nav: { home: "Home", services: "Services", process: "Process", compatibility: "Compatibility", testimonials: "Testimonials", about: "About", contact: "Contact", quote: "Get a quote", packages: "Packages", portfolio: "Portfolio" },
      hero: { title: "", subtitle: "Professional web design and development services", cta: "Let's create your dream website together", servicesBtn: "Our services", contactBtn: "Contact us" },
      services: { 
        title: "Our Services", 
        description: "We offer comprehensive web design and development services tailored to your needs.",
        webDesign: { title: "Web Design", description: "Modern, responsive and aesthetic website designs.", items: ["UI/UX Design","RWD Responsiveness","Conversion Optimization","Graphic Design"] },
        webDev: { title: "Web Development", description: "Website programming using the latest technologies.", items: ["Websites and online stores","Web applications","System integrations","Performance optimization"] },
        seo: { title: "SEO Optimization", description: "Increase your website's visibility in search results.", items: ["SEO Audit","Content Optimization","Link Building","Competitor Analysis"] },
        maintenance: { title: "Support and Maintenance", description: "Comprehensive care for your website after launch.", items: ["Updates and Backup","Security Monitoring","Technical Support","Performance Reports"] }
      },
      packages: {
        title: "Example Packages",
        description: "We offer different packages tailored to your needs and budget",
        basic: {
          title: "Basic",
          price: "from 500 GBP",
          description: "Ideal for small businesses and individuals",
          features: ["One-page website", "Responsive design", "Basic SEO optimization", "Contact form integration", "Basic technical support"]
        },
        pro: {
          title: "Pro",
          price: "from 800 GBP",
          description: "Perfect for growing businesses",
          features: ["Multi-page website", "Advanced design", "Advanced SEO optimization", "Social media integration", "Contact forms and newsletter", "7 days technical support"]
        },
        premium: {
          title: "Premium",
          price: "from 1350 GBP",
          description: "Comprehensive solution for demanding clients",
          features: ["Multi-page website with admin panel", "Advanced CMS system", "Full SEO optimization", "Payment system integration", "Analytics and reporting", "7 days technical support", "Panel operation training"]
        }
      },
      portfolio: {
        title: "My Projects",
        description: "Check out my latest GitHub projects",
        viewProject: "View project",
        viewCode: "Source code",
        technologies: "Technologies"
      },
      process: {
        title: "Our Process",
        steps: [
          { title: "Consultation and Analysis", description: "We discuss your needs, goals and expectations.", icon: "fas fa-comments" },
          { title: "Concept Design", description: "We prepare a mockup and graphic design of the website.", icon: "fas fa-pencil-ruler" },
          { title: "Implementation and Coding", description: "We program the website according to previous arrangements.", icon: "fas fa-code" },
          { title: "Testing and Optimization", description: "We test the website on different devices and browsers.", icon: "fas fa-check-double" },
          { title: "Launch and Training", description: "We launch the website and train you in its operation.", icon: "fas fa-rocket" },
          { title: "After-sales Support", description: "We provide technical support after project completion.", icon: "fas fa-headset" }
        ]
      },
      compatibility: {
        title: "Full Compatibility",
        description: "Our websites work perfectly on all devices and browsers",
        subtitle: "We guarantee optimal performance on every device"
      },
      about: { 
        title: "About Us", 
        description1: "NOVA Witryny is a team of experienced designers and developers passionate about creating modern websites.", 
        description2: "We specialize in creating responsive, efficient and functional web solutions that deliver real business benefits.", 
        stats: [
          { value: "120+", label: "Completed projects" },
          { value: "7+", label: "Years of experience" },
          { value: "98%", label: "Satisfied clients" },
          { value: "24h", label: "Technical support" }
        ]
      },
      contact: { 
        title: "Contact", 
        description: "Get in touch with us to discuss your project.", 
        infoTitle: "Contact", 
        phone: "Phone", 
        email: "Email", 
        address: "Address", 
        formTitle: "Send inquiry", 
        name: "Full name", 
        company: "Company", 
        message: "Project description", 
        send: "Send inquiry" 
      },
      footer: { 
        description: "Professional web design and development services.", 
        services: "Services", 
        company: "Company", 
        hours: "Opening Hours", 
        rights: "All rights reserved. Site by NOVA Witryny" 
      }
    }
  };

  const t = translations[currentLanguage];

  const ListItem: React.FC<{ item: string }> = ({ item }) => (
    <li className="flex items-center opacity-80 group-hover:opacity-100 transition-opacity duration-300">
      <i className="fas fa-check text-blue-600 mr-2 transform group-hover:scale-110 transition-transform duration-300"></i>
      <span className="text-gray-700">{item}</span>
    </li>
  );

  const ServiceCard: React.FC<{ icon: string; title: string; description: string; items: string[] }> = ({ icon, title, description, items }) => (
    <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 border border-gray-200 hover:-translate-y-1 group cursor-pointer">
      <div className="text-4xl mb-4 transition-transform duration-300">{icon}</div>
      <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-blue-600 transition-colors duration-300">{title}</h3>
      <p className="text-gray-600 mb-4">{description}</p>
      <ul className="space-y-2">
        {items.map((item,i) => (
          <li key={i} className="flex items-center opacity-80 transition-opacity duration-300">
            <i className="fas fa-check text-blue-600 mr-2"></i>
            <span className="text-gray-700">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  const ProcessStep: React.FC<{ step: { title: string; description: string; icon: string }; index: number }> = ({ step, index }) => (
    <div className="flex flex-col items-center text-center">
      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 text-blue-600 text-2xl">
        <i className={step.icon}></i>
      </div>
      <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-100 w-full min-h-[200px] flex flex-col">
        <div className="text-blue-600 font-bold text-lg mb-2">Krok {index + 1}</div>
        <h3 className="font-bold text-xl mb-2 text-gray-900">{step.title}</h3>
        <p className="text-gray-600 flex-grow">{step.description}</p>
      </div>
    </div>
  );

  const BrowserIcon: React.FC<{ browser: { name: string; icon: string; color: string } }> = ({ browser }) => (
    <div className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
      <div className="text-4xl mb-2 transition-transform duration-300" style={{ color: browser.color }}>
        <i className={browser.icon}></i>
      </div>
      <span className="text-sm font-medium text-gray-700">{browser.name}</span>
    </div>
  );

  // Komponent karuzeli projektów
  const ProjectCarousel: React.FC = () => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % githubProjects.length);
      }, 3000);

      return () => clearInterval(interval);
    }, []);

    const currentProject = githubProjects[currentIndex];

    return (
      <div className="relative bg-gradient-to-br from-purple-900 to-blue-800 rounded-2xl p-8 h-96 flex flex-col justify-between">
        {/* <div className="absolute inset-0 bg-black opacity-20 rounded-2xl"></div> */}
        
        {/* Tło z obrazem projektu */}
        <div 
          className="absolute inset-0 bg-cover bg-center rounded-2xl opacity-20"
          style={{ backgroundImage: `url(${currentProject.image})` }}
        ></div>
        
        <div className="relative z-10 flex-1 flex flex-col justify-center">
          <h3 className="text-2xl font-bold text-white mb-3">{currentProject.name}</h3>
          <p className="text-gray-200 mb-4">{currentProject.description}</p>
          
          <div className="mb-4">
            <h4 className="text-white font-semibold mb-2">{t.portfolio.technologies}:</h4>
            <div className="flex flex-wrap gap-2">
              {currentProject.technologies.map((tech, index) => (
                <span key={index} className="bg-white bg-opacity-20 text-white px-3 py-1 rounded-full text-sm">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="relative z-10 flex gap-4">
          <a 
            href={currentProject.githubUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex-1 bg-white text-purple-900 hover:bg-gray-100 py-3 px-6 rounded-lg font-semibold transition-all duration-300 text-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <i className="fab fa-github mr-2"></i>
            {t.portfolio.viewCode}
          </a>
          {currentProject.demoUrl && (
            <a 
              href={currentProject.demoUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex-1 bg-transparent border-2 border-white text-white hover:bg-white hover:text-purple-900 py-3 px-6 rounded-lg font-semibold transition-all duration-300 text-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <i className="fas fa-external-link-alt mr-2"></i>
              {t.portfolio.viewProject}
            </a>
          )}
        </div>

        {/* Wskaźniki karuzeli */}
        <div className="relative z-10 flex justify-center mt-6 space-x-2">
          {githubProjects.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex ? 'bg-white' : 'bg-white bg-opacity-30'
              }`}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50">
      {/* Loading Screen */}
      {isLoading && (
        <div className="fixed inset-0 flex items-center justify-center bg-blue-600 z-50 transition-opacity duration-500">
          <div className="relative">
            <div className="w-28 h-28 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            <img src={Logotyp3} alt="NOVA Witryny" className="h-8 w-auto absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 filter invert brightness-200" />
          </div>
        </div>
      )}

      {/* Header */}
      <header className={`fixed w-full z-40 transition-all duration-300 ${isScrolled ? 'bg-gray-900 shadow-xl py-2' : 'bg-transparent py-4'}`}>
        <div className="container mx-auto px-4 flex justify-between items-center">
          <a href="#" onClick={(e)=>{e.preventDefault(); window.scrollTo({top:0,behavior:'smooth'});}} className="flex items-center">
            <img src={Logotyp3} alt="NOVA Witryny" className="h-12 md:h-16 w-auto filter brightness-0 invert"/>
          </a>

          {/* Desktop Navigation with GooeyNav */}
          <div className="hidden md:flex items-center">
            <div className="mr-8" style={{ height: '50px', position: 'relative' }}>
              <GooeyNav
                items={[
                  { label: t.nav.home, href: "#" },
                  { label: t.nav.services, href: "#uslugi" },
                  { label: t.nav.packages, href: "#pakiety" },
                  { label: t.nav.portfolio, href: "#portfolio" },
                  { label: t.nav.process, href: "#proces" },
                  { label: t.nav.compatibility, href: "#kompatybilnosc" },
                  { label: t.nav.about, href: "#onas" },
                  { label: t.nav.contact, href: "#kontakt" }
                ]}
                onItemClick={(href) => {
                  if (href === "#") {
                    window.scrollTo({top:0,behavior:'smooth'});
                  } else {
                    const targetElement = document.querySelector(href);
                    if (targetElement) {
                      window.scrollTo({
                        top: targetElement.getBoundingClientRect().top + window.pageYOffset - 80,
                        behavior: 'smooth'
                      });
                    }
                  }
                  setIsMobileMenuOpen(false);
                }}
              />
            </div>

            {/* Language Selector */}
            <div className="relative">
              <button 
                onClick={toggleLangDropdown}
                className="flex items-center text-white hover:text-blue-300 transition-colors duration-200"
              >
                <span className={`fi fi-${currentLanguage === 'PL' ? 'pl' : currentLanguage === 'DE' ? 'de' : 'gb'} mr-2`}></span>
                {currentLanguage}
                <i className="fas fa-chevron-down ml-2 text-xs"></i>
              </button>
              
              {isLangDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-32 bg-white rounded-lg shadow-xl border border-gray-200 py-2">
                  <button 
                    onClick={() => selectLanguage('PL')}
                    className="flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                  >
                    <span className="fi fi-pl mr-3"></span>
                    Polski
                  </button>
                  <button 
                    onClick={() => selectLanguage('DE')}
                    className="flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                  >
                    <span className="fi fi-de mr-3"></span>
                    Deutsch
                  </button>
                  <button 
                    onClick={() => selectLanguage('EN')}
                    className="flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                  >
                    <span className="fi fi-gb mr-3"></span>
                    English
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={toggleMobileMenu}
            className="md:hidden text-white text-2xl z-50"
          >
            <i className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-0 left-0 w-full h-screen bg-gray-900 pt-20 px-4">
            <div className="flex flex-col space-y-6 text-white text-lg">
              {[
                { href: "#", label: t.nav.home },
                { href: "#uslugi", label: t.nav.services },
                { href: "#pakiety", label: t.nav.packages },
                { href: "#portfolio", label: t.nav.portfolio },
                { href: "#proces", label: t.nav.process },
                { href: "#kompatybilnosc", label: t.nav.compatibility },
                { href: "#onas", label: t.nav.about },
                { href: "#kontakt", label: t.nav.contact }
              ].map((item) => (
                <a 
                  key={item.href}
                  href={item.href} 
                  onClick={(e) => handleSmoothScroll(e, item.href)}
                  className="py-3 px-4 hover:bg-blue-600 rounded-lg transition-colors duration-200"
                >
                  {item.label}
                </a>
              ))}
              
              <div className="flex space-x-4 pt-4 border-t border-gray-700">
                <button onClick={() => selectLanguage('PL')} className="flex items-center">
                  <span className="fi fi-pl mr-2"></span>
                  PL
                </button>
                <button onClick={() => selectLanguage('DE')} className="flex items-center">
                  <span className="fi fi-de mr-2"></span>
                  DE
                </button>
                <button onClick={() => selectLanguage('EN')} className="flex items-center">
                  <span className="fi fi-gb mr-2"></span>
                  EN
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center bg-black overflow-hidden" ref={observerRefs.hero} data-section="hero">
        <div className="absolute inset-0">
          <Silk
            speed={5}
            scale={1}
            color="#5c23c7ff"
            noiseIntensity={0}
            rotation={0}
          />
        </div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <img src={Logotyp3} alt="NOVA Witryny" className="h-24 md:h-32 w-auto mx-auto mb-8 filter brightness-0 invert opacity-90"/>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">{t.hero.title}</h1>
          <p className="text-xl md:text-2xl mb-8 leading-relaxed opacity-90">{t.hero.subtitle}</p>
          
          {/* Zastosowanie efektu TextType dla tekstu CTA */}
          <div className="text-lg md:text-xl mb-12 max-w-3xl mx-auto leading-relaxed opacity-80 min-h-[60px] flex items-center justify-center">
            <TextType 
              text={[t.hero.cta]}
              typingSpeed={75}
              pauseDuration={1500}
              showCursor={true}
              cursorCharacter="|"
              className="text-center"
              as="p"
            />
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#uslugi" onClick={(e)=>handleSmoothScroll(e,'#uslugi')} className="bg-white text-blue-900 hover:bg-blue-100 px-8 py-3 rounded-lg font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
              {t.hero.servicesBtn}
            </a>
            <a href="#kontakt" onClick={(e)=>handleSmoothScroll(e,'#kontakt')} className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-3 rounded-lg font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
              {t.hero.contactBtn}
            </a>
          </div>
        </div>
        
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce z-10">
          <a href="#uslugi" onClick={(e)=>handleSmoothScroll(e,'#uslugi')} className="text-white text-2xl">
            <i className="fas fa-chevron-down"></i>
          </a>
        </div>
      </section>

      {/* Services Section */}
      <section 
        id="uslugi" 
        ref={observerRefs.services}
        data-section="services"
        className="py-20 bg-white"
      >
        <div className="container mx-auto px-4">
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible.services ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.services.title}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t.services.description}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <ServiceCard 
              icon="🎨" 
              title={t.services.webDesign.title} 
              description={t.services.webDesign.description}
              items={t.services.webDesign.items}
            />
            <ServiceCard 
              icon="💻" 
              title={t.services.webDev.title} 
              description={t.services.webDev.description}
              items={t.services.webDev.items}
            />
            <ServiceCard 
              icon="🔍" 
              title={t.services.seo.title} 
              description={t.services.seo.description}
              items={t.services.seo.items}
            />
            <ServiceCard 
              icon="⚙️" 
              title={t.services.maintenance.title} 
              description={t.services.maintenance.description}
              items={t.services.maintenance.items}
            />
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section 
        id="pakiety" 
        ref={observerRefs.packages}
        data-section="packages"
        className="py-20 bg-gradient-to-br from-blue-50 to-purple-50"
      >
        <div className="container mx-auto px-4">
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible.packages ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.packages.title}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t.packages.description}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Basic Package */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.packages.basic.title}</h3>
                  <div className="text-3xl font-bold text-blue-600 mb-2">{t.packages.basic.price}</div>
                  <p className="text-gray-600">{t.packages.basic.description}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {t.packages.basic.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <i className="fas fa-check text-green-500 mr-3"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <a 
                  href="#kontakt" 
                  onClick={(e) => handleSmoothScroll(e, '#kontakt')}
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white text-center py-3 px-6 rounded-lg font-semibold transition-colors duration-300"
                >
                  {t.nav.quote}
                </a>
              </div>
            </div>

            {/* Pro Package */}
            <div className="bg-white rounded-2xl shadow-xl border-2 border-blue-500 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 transform scale-105 relative">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-500 text-white px-6 py-2 rounded-full font-semibold">
                Popularny
              </div>
              <div className="p-8 pt-12">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.packages.pro.title}</h3>
                  <div className="text-3xl font-bold text-blue-600 mb-2">{t.packages.pro.price}</div>
                  <p className="text-gray-600">{t.packages.pro.description}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {t.packages.pro.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <i className="fas fa-check text-green-500 mr-3"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <a 
                  href="#kontakt" 
                  onClick={(e) => handleSmoothScroll(e, '#kontakt')}
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white text-center py-3 px-6 rounded-lg font-semibold transition-colors duration-300"
                >
                  {t.nav.quote}
                </a>
              </div>
            </div>

            {/* Premium Package */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.packages.premium.title}</h3>
                  <div className="text-3xl font-bold text-blue-600 mb-2">{t.packages.premium.price}</div>
                  <p className="text-gray-600">{t.packages.premium.description}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {t.packages.premium.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <i className="fas fa-check text-green-500 mr-3"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <a 
                  href="#kontakt" 
                  onClick={(e) => handleSmoothScroll(e, '#kontakt')}
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white text-center py-3 px-6 rounded-lg font-semibold transition-colors duration-300"
                >
                  {t.nav.quote}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section 
        id="portfolio" 
        ref={observerRefs.portfolio}
        data-section="portfolio"
        className="py-20 bg-white"
      >
        <div className="container mx-auto px-4">
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible.portfolio ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.portfolio.title}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t.portfolio.description}</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <ProjectCarousel />
          </div>

          {/* Grid z wszystkimi projektami */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
            {githubProjects.map((project, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group cursor-pointer"
                onClick={() => window.open(project.githubUrl, '_blank')}
              >
                <div className="h-48 relative overflow-hidden">
                  {/* USUŃ nakładkę i pokaż czysty obraz */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${project.image})` }}
                  ></div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.name}</h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{project.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                        {tech}
                      </span>
                    ))}
                  </div>
                  <div className="flex justify-between items-center">
                    <a 
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-blue-600 hover:text-blue-700 font-semibold flex items-center"
                    >
                      <i className="fab fa-github mr-2"></i>
                      {t.portfolio.viewCode}
                    </a>
                    {project.demoUrl && (
                      <a 
                        href={project.demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-gray-600 hover:text-gray-700 flex items-center"
                      >
                        <i className="fas fa-external-link-alt mr-2"></i>
                        {t.portfolio.viewProject}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section 
        id="proces" 
        ref={observerRefs.process}
        data-section="process"
        className="py-20 bg-gradient-to-br from-blue-50 to-purple-50"
      >
        <div className="container mx-auto px-4">
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible.process ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.process.title}</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {t.process.steps.map((step, index) => (
              <ProcessStep key={index} step={step} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Compatibility Section */}
      <section 
        id="kompatybilnosc" 
        ref={observerRefs.compatibility}
        data-section="compatibility"
        className="py-20 bg-white"
      >
        <div className="container mx-auto px-4">
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible.compatibility ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.compatibility.title}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t.compatibility.description}</p>
            <p className="text-lg text-gray-500 mt-4">{t.compatibility.subtitle}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 max-w-4xl mx-auto">
            {browsers.map((browser, index) => (
              <BrowserIcon key={index} browser={browser} />
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section 
        id="onas" 
        ref={observerRefs.about}
        data-section="about"
        className="py-20 bg-gradient-to-br from-blue-900 to-purple-900 text-white"
      >
        <div className="container mx-auto px-4">
          <div className={`max-w-6xl mx-auto transition-all duration-1000 ${isVisible.about ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">{t.about.title}</h2>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-xl mb-6 leading-relaxed">{t.about.description1}</p>
                <p className="text-lg opacity-90 leading-relaxed">{t.about.description2}</p>
                
                <div className="grid grid-cols-2 gap-6 mt-8">
                  {t.about.stats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className="text-3xl font-bold text-blue-300 mb-2">{stat.value}</div>
                      <div className="text-gray-200">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-white bg-opacity-10 rounded-2xl p-8 backdrop-blur-sm border border-white border-opacity-20">
                <h3 className="text-2xl font-bold mb-6 text-center">Dlaczego my?</h3>
                <div className="space-y-4">
                  {[
                    { icon: "🎯", text: "Indywidualne podejście do każdego projektu" },
                    { icon: "⚡", text: "Nowoczesne technologie i najlepsze praktyki" },
                    { icon: "🛡️", text: "Bezpieczeństwo i niezawodność" },
                    { icon: "📈", text: "Skuteczne rozwiązania biznesowe" },
                    { icon: "💬", text: "Przejrzysta komunikacja i wsparcie" },
                    { icon: "🚀", text: "Szybkie terminy realizacji" }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <div className="text-2xl">{item.icon}</div>
                      <span className="text-lg">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section 
        id="kontakt" 
        ref={observerRefs.contact}
        data-section="contact"
        className="py-20 bg-white"
      >
        <div className="container mx-auto px-4">
          <div className={`max-w-6xl mx-auto transition-all duration-1000 ${isVisible.contact ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.contact.title}</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t.contact.description}</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{t.contact.infoTitle}</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                      <i className="fas fa-phone"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{t.contact.phone}</h4>
                      <p className="text-gray-600">+48 727 149 151</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                      <i className="fas fa-envelope"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{t.contact.email}</h4>
                      <p className="text-gray-600">witryny@novahls.pl</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                      <i className="fas fa-map-marker-alt"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{t.contact.address}</h4>
                      <p className="text-gray-600">ul. Stanisławowska 47, 54-611 Wrocław, Polska</p>
                      <p className="text-gray-600">NIP: 8943267784</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border border-blue-100">
                  <h4 className="font-bold text-gray-900 mb-3">Godziny pracy</h4>
                  <div className="space-y-2 text-gray-700">
                    <div className="flex justify-between">
                      <span>Poniedziałek - Piątek:</span>
                      <span className="font-semibold">10:00 - 16:00</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sobota:</span>
                      <span className="font-semibold">Zamknięte</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Niedziela:</span>
                      <span className="font-semibold">Zamknięte</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{t.contact.formTitle}</h3>
                
                {isFormSubmitted ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                    <i className="fas fa-check-circle text-green-500 text-4xl mb-4"></i>
                    <h4 className="text-xl font-bold text-green-800 mb-2">Wiadomość wysłana!</h4>
                    <p className="text-green-700">Dziękujemy za kontakt. Odpowiemy najszybciej jak to możliwe.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        {t.contact.name}
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                        placeholder="Jan Kowalski"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                        placeholder="jan@przyklad.pl"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                        {t.contact.company}
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                        placeholder="Nazwa firmy (opcjonalnie)"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        {t.contact.message}
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 resize-vertical"
                        placeholder="Opisz swój projekt..."
                      ></textarea>
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-300 ${
                        isSubmitting 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:bg-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                      }`}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Wysyłanie...
                        </span>
                      ) : (
                        t.contact.send
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img src={Logotyp3} alt="NOVA Witryny" className="h-12 w-auto mb-4 filter brightness-0 invert" />
              <p className="text-gray-400 mb-4">{t.footer.description}</p>
              <div className="flex space-x-4">
                <a href="https://www.linkedin.com/in/michał-szczepański-7235b4190" className="text-gray-400 hover:text-white transition-colors duration-200">
                  <i className="fab fa-linkedin-in"></i>
                </a>
                <a href="https://github.com/CBTEnjoyer" className="text-gray-400 hover:text-white transition-colors duration-200">
                  <i className="fab fa-github"></i>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold text-lg mb-4">{t.footer.services}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#uslugi" onClick={(e) => handleSmoothScroll(e, '#uslugi')} className="hover:text-white transition-colors duration-200">Web Design</a></li>
                <li><a href="#uslugi" onClick={(e) => handleSmoothScroll(e, '#uslugi')} className="hover:text-white transition-colors duration-200">Web Development</a></li>
                <li><a href="#uslugi" onClick={(e) => handleSmoothScroll(e, '#uslugi')} className="hover:text-white transition-colors duration-200">SEO Optimization</a></li>
                <li><a href="#uslugi" onClick={(e) => handleSmoothScroll(e, '#uslugi')} className="hover:text-white transition-colors duration-200">Support & Maintenance</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-lg mb-4">{t.footer.company}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#onas" onClick={(e) => handleSmoothScroll(e, '#onas')} className="hover:text-white transition-colors duration-200">{t.nav.about}</a></li>
                <li><a href="#proces" onClick={(e) => handleSmoothScroll(e, '#proces')} className="hover:text-white transition-colors duration-200">{t.nav.process}</a></li>
                <li><a href="#portfolio" onClick={(e) => handleSmoothScroll(e, '#portfolio')} className="hover:text-white transition-colors duration-200">{t.nav.portfolio}</a></li>
                <li><a href="#kontakt" onClick={(e) => handleSmoothScroll(e, '#kontakt')} className="hover:text-white transition-colors duration-200">{t.nav.contact}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-lg mb-4">{t.footer.hours}</h4>
              <div className="space-y-2 text-gray-400">
                <div className="flex justify-between">
                  <span>Pon - Pt:</span>
                  <span>8:00 - 18:00</span>
                </div>
                <div className="flex justify-between">
                  <span>Sobota:</span>
                  <span>10:00 - 14:00</span>
                </div>
                <div className="flex justify-between">
                  <span>Niedziela:</span>
                  <span>Zamknięte</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} NOVA Witryny. {t.footer.rights}</p>
          </div>
        </div>
      </footer>

      {/* Cookie Consent */}
      <CookieConsent
        location="bottom"
        buttonText="Rozumiem"
        cookieName="novaWitrynyCookie"
        style={{ background: "#1f2937", fontSize: "14px" }}
        buttonStyle={{ background: "#3b82f6", color: "white", fontSize: "14px", borderRadius: "6px", padding: "8px 16px" }}
        expires={150}
      >
        Ta strona używa plików cookies w celu poprawnego działania strony.{" "}
        <a href="#/polityka-prywatnosci" className="text-blue-300 hover:text-blue-100 underline ml-1">
          Dowiedz się więcej
        </a>
      </CookieConsent>
    </div>
  );
};

export default App;